import type { ChangeEmitter, Store } from "@/data/repositories";
import { detectLocale } from "@/app/i18n/detect";
import { strings } from "@/app/i18n/strings";
import { pickText } from "@/domain/localized";
import type { ReminderState } from "../ReminderService";
import type { LocalReminder, NotificationScheduler, ReminderBackend } from "./types";

interface Deps {
  scheduler: NotificationScheduler;
  store: Store;
  changeSources: readonly ChangeEmitter[];
  language?: string;
}

/**
 * Reminders as on-device local notifications (the native build). No server and
 * no network: each enabled scenario gets one notification repeating daily at its
 * local time. `settings.localReminders` holds the on/paused state; pausing cancels
 * every scheduled notification but keeps the flag so enabling restores them.
 */
export class LocalNotificationReminderService implements ReminderBackend {
  lastError: string | null = null;
  /** Syncs run one after another so two "cancel, then schedule" passes never interleave. */
  private queue: Promise<void> = Promise.resolve();

  constructor(private readonly deps: Deps) {}

  /** Nothing to warm up on the device. */
  prepare(): Promise<unknown> {
    return Promise.resolve();
  }

  async state(): Promise<ReminderState> {
    const permission = await this.deps.scheduler.permission();
    if (permission === "denied") return "denied";
    if (permission === "prompt") return "prompt";
    const settings = await this.deps.store.settings.get();
    if (!settings.localReminders) return "granted";
    return settings.localReminders.paused ? "paused" : "enabled";
  }

  /** Asks for permission if needed, marks reminders on, and schedules them. */
  async enable(): Promise<ReminderState> {
    this.lastError = null;
    try {
      const { scheduler } = this.deps;
      if ((await scheduler.permission()) !== "granted") {
        if ((await scheduler.requestPermission()) !== "granted") return this.state();
      }
      await this.deps.store.settings.update({ localReminders: { paused: false } });
      await this.sync();
      return "enabled";
    } catch (error) {
      this.lastError = describe(error);
      return this.state();
    }
  }

  /** Turns reminders off: keep the flag, cancel every scheduled notification. */
  async pause(): Promise<void> {
    await this.deps.store.settings.update({ localReminders: { paused: true } });
    await this.deps.scheduler.cancelAll();
  }

  /** Reschedules one daily notification per enabled scenario, or cancels all when off/paused. */
  sync(): Promise<void> {
    const run = this.queue.then(() => this.syncNow());
    this.queue = run.catch(() => undefined);
    return run;
  }

  /** Syncs, then reads back what the device holds: the truth Settings shows, not what we meant to schedule. */
  async upcoming(): Promise<LocalReminder[]> {
    await this.sync();
    const pending = await this.deps.scheduler.pending();
    return [...pending].sort((a, b) => a.time.localeCompare(b.time));
  }

  private async syncNow(): Promise<void> {
    const settings = await this.deps.store.settings.get();
    if (!settings.localReminders || settings.localReminders.paused) {
      await this.deps.scheduler.cancelAll().catch(() => undefined);
      return;
    }
    const locale = settings.locale ?? detectLocale(this.deps.language);
    const t = strings[locale];
    const [subscriptions, scenarios] = await Promise.all([
      this.deps.store.subscriptions.listAll(),
      this.deps.store.scenarios.list(),
    ]);
    const reminders: LocalReminder[] = subscriptions
      .filter((s) => s.enabled)
      .map((s) => {
        const scenario = scenarios.find((x) => x.id === s.scenarioId);
        return {
          id: notificationId(s.id),
          time: s.reminderTime,
          title: scenario ? pickText(scenario.name, locale) : t.appName,
          body: t.reminders.pushBody,
        };
      });
    try {
      await this.deps.scheduler.schedule(reminders);
      this.lastError = null;
    } catch (error) {
      this.lastError = describe(error);
    }
  }

  /** Local notifications repeat daily; there is no per-day "done" to record on a device. */
  async markDone(): Promise<void> {
    return Promise.resolve();
  }

  start(): () => void {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void this.sync(), 1500);
    };
    const unsubscribes = this.deps.changeSources.map((source) => source.subscribe(schedule));
    void this.sync();
    return () => {
      for (const u of unsubscribes) u();
      if (timer) clearTimeout(timer);
    };
  }
}

/** A stable positive 31-bit id from a subscription id (local-notification ids are integers). */
function notificationId(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  return (Math.abs(hash) % 2147483647) || 1;
}

function describe(error: unknown): string {
  if (error instanceof Error) return error.name && error.name !== "Error" ? `${error.name}: ${error.message}` : error.message;
  return String(error);
}
